import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout, { Content, Footer, Header } from 'antd/es/layout/layout';

import HeaderComponent from './components/Header';
import FooterComponent from './components/Footer';
import { styles } from './styles';
import { HOME_PATH, CATEGORY_PATH_PREFIX } from './utils/constant';
import Loader from './components/common/Loader';

const Fixture = lazy(() => import('./views/Fixture'));
const CurrentVersion = lazy(() => import('./views/Landing/currentVersion'));
const Standings = lazy(() => import('./views/Standings'));
const FixtureList = lazy(() => import('./views/FixtureList'));
const AddStreamComponent = lazy(() => import('./components/addStream'));
const UpdateStreamComponent = lazy(() => import('./components/UpdateStream'));
const AddMatchComponent = lazy(() => import('./components/AddMatch'));
const ContactUsComponent = lazy(() => import('./components/ContactUs'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const AboutUs = lazy(() => import('./components/AboutUs'));

const footerStyle: React.CSSProperties = {
  textAlign: 'center',
  color: '#fff',
  backgroundColor: '#95100d',
};

/**
 * Handles single-segment category URLs:
 * - /streams-UFC  → redirect to /streameast-UFC
 * - /streameast-UFC → category page
 *
 * React Router params are whole segments, so /streameast-:category cannot
 * match /streameast-Soccer — we use /:categorySlug instead.
 */
function CategorySlugRoute() {
  const { categorySlug } = useParams();
  const slug = categorySlug ?? '';

  if (slug.startsWith('streams-')) {
    const sport = slug.slice('streams-'.length);
    return <Navigate to={`/${CATEGORY_PATH_PREFIX}-${sport}`} replace />;
  }

  if (slug.startsWith(`${CATEGORY_PATH_PREFIX}-`)) {
    return <CurrentVersion />;
  }

  return <Navigate to={HOME_PATH} replace />;
}

const Router = () => {
  return (
    <Layout>
      <Header style={styles.header}>
        <HeaderComponent />
      </Header>

      <Content style={{ background: '#222121', textAlign: 'center', minHeight: '100vh' }}>
        <Suspense
          fallback={
            <div style={{ color: '#000', padding: '2rem' }}>
              <Loader />
            </div>
          }
        >
          <Routes>
            {/* Home */}
            <Route path='/' element={<Navigate to={HOME_PATH} replace />} />
            <Route path='/v1' element={<CurrentVersion />} />

            {/* Fixtures & admin — before /:categorySlug */}
            <Route path='/fixture/:teams' element={<Fixture />} />
            <Route path='/fixture/:teams/add' element={<AddStreamComponent />} />
            <Route path='/fixture/:teams/update' element={<UpdateStreamComponent />} />
            <Route path='/leagues/standings' element={<Standings />} />
            <Route path='/leagues/:leagueName/:id' element={<FixtureList />} />
            <Route path='/teams/:teamName/:id' element={<FixtureList />} />
            <Route path='/add' element={<AddMatchComponent />} />

            {/* Static */}
            <Route path='/about-us' element={<AboutUs />} />
            <Route path='/contact' element={<ContactUsComponent />} />
            <Route path='/privacy-policy' element={<PrivacyPolicy />} />

            {/* /streameast-Soccer, /streams-Soccer (legacy redirect) */}
            <Route path='/:categorySlug' element={<CategorySlugRoute />} />

            <Route path='*' element={<Navigate to={HOME_PATH} replace />} />
          </Routes>
        </Suspense>
      </Content>

      <Footer style={footerStyle}>
        <FooterComponent />
      </Footer>
    </Layout>
  );
};

export default Router;
